// connect/resolve.ts — a tool named in free words → a real CONNECT_TOOLS entry.
//
// ONE table, read by both sides: netti's connect tools on the server
// (lib/netti/tools/connect.ts re-exports resolveClient) and the connect walk in
// netti's rail in the browser (lib/connect/walk.ts), so "set me up with claude
// code" lands on the same tool wherever it is typed. Pure: no server imports,
// so the rail can carry it without the docs index the server's tokenizer sits in.

import { connectTool, type ConnectTool } from './tools'

/** lowercase whole words, lightly stemmed ("settings" ↔ "setting"). The same
 *  rule as netti's page index (lib/netti/tools/pages tokens), kept here so this
 *  module stays browser-safe. */
const stem = (w: string) => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w)
export function connectTokens(s: string): string[] {
  return (s ?? '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map(stem)
}

interface Alias {
  id: string
  /** a key starting with `=` matches only when it is the WHOLE ask ("continue", "cc"):
   *  a word that is also an ordinary verb must never pick a tool from inside a sentence */
  keys: string[]
  /** something true the person needs to hear with this answer */
  note?: string
}

export const GEMINI_NOTE =
  'the gemini app can take innernet as a custom app, added on the web: settings, connected apps, add a custom app, then the link (personal google accounts only, for now). gemini cli is the steadier way in (antigravity works too).'

const ALIASES: Alias[] = [
  { id: 'claude-code', keys: ['claude code', 'claudecode', 'claude cli', '=cc'] },
  { id: 'claude-desktop', keys: ['claude desktop', 'claude app', 'claude ai', 'claude web', 'claude mobile', 'claude for desktop', 'claude chat', 'claude', 'anthropic'] },
  { id: 'chatgpt', keys: ['chatgpt', 'chat gpt', 'chatgpt plugin', 'chatgpt plugins', 'openai', 'gpt', 'gpt 4', 'gpt 5'] },
  { id: 'cursor', keys: ['cursor', 'cursor ide', 'cursor editor'] },
  { id: 'vscode', keys: ['vs code', 'vscode', 'visual studio code', 'copilot', 'github copilot'] },
  { id: 'windsurf', keys: ['windsurf', 'codeium', 'cascade'] },
  { id: 'zed', keys: ['zed', 'zed editor'] },
  { id: 'cline', keys: ['cline'] },
  { id: 'continue', keys: ['continue dev', 'continue extension', '=continue'] },
  { id: 'antigravity', keys: ['antigravity', 'google antigravity', 'anti gravity'] },
  { id: 'gemini-cli', keys: ['gemini cli', 'gemini command line', 'gemini terminal'] },
  { id: 'gemini-cli', keys: ['gemini', 'google gemini', 'gemini app', 'gemini web', 'bard'], note: GEMINI_NOTE },
  { id: 'codex', keys: ['codex', 'openai codex', 'codex cli'] },
  { id: 'notion', keys: ['notion', 'notion ai', 'notion agent', 'notion custom agent'] },
  { id: 'grok-bot', keys: ['grok bot', 'grokbot', 'grok bots', 'grok agent', 'xai bot'] },
  { id: 'grok', keys: ['grok', 'xai', 'x ai', 'grok com', 'supergrok'] },
  { id: 'perplexity', keys: ['perplexity', 'perplexity ai', 'comet'] },
  { id: 'mistral', keys: ['le chat', 'lechat', 'mistral', 'mistral ai'] },
  { id: 'openclaw', keys: ['openclaw', 'open claw', 'clawdbot', 'moltbot'] },
  { id: 'hermes', keys: ['hermes', 'hermes agent', 'nous', 'nous research', 'nousresearch'] },
  { id: 'other', keys: ['other', 'any other', 'any client', 'any mcp client', 'mcp client', 'generic', 'custom', 'something else'] },
]

/** the words that carry no tool when the whole ask is one key */
const LEAD = new Set(
  ['the', 'a', 'an', 'my', 'please', 'connect', 'set', 'setup', 'up', 'add', 'install', 'wire', 'with', 'innernet', 'mcp', 'to', 'in', 'on', 'for', 'me', 'i', 'want', 'need', 'help', 'use', 'using', 'get', 'it', 'how', 'do', 'can', 'you'].flatMap(connectTokens),
)

export interface ClientHit {
  tool: ConnectTool
  note?: string
  /** true when nothing matched and the open door answers instead */
  unknown?: boolean
}

/** the best alias named anywhere in these words, or null. Whole words, longest phrase wins;
 *  a short key counts only as the whole ask. */
function bestAlias(text: string): Alias | null {
  const toks = connectTokens(text)
  if (!toks.length) return null
  const whole = toks.filter((t) => !LEAD.has(t)).join(' ')
  let best: { alias: Alias; rank: number; at: number } | null = null
  for (const alias of ALIASES) {
    for (const key of alias.keys) {
      const exactOnly = key.startsWith('=')
      const kt = connectTokens(exactOnly ? key.slice(1) : key)
      if (!kt.length) continue
      const joined = kt.join(' ')
      let at = -1
      if (exactOnly) at = whole === joined ? 0 : -1
      else {
        outer: for (let i = 0; i + kt.length <= toks.length; i++) {
          for (let j = 0; j < kt.length; j++) if (toks[i + j] !== kt[j]) continue outer
          at = i
          break
        }
      }
      if (at < 0) continue
      const rank = kt.length * 1000 + joined.length
      if (!best || rank > best.rank || (rank === best.rank && at < best.at)) best = { alias, rank, at }
    }
  }
  return best?.alias ?? null
}

/** a tool named in free words → a real CONNECT_TOOLS entry. Null only for an empty ask;
 *  anything unrecognised is the open door (`other`, marked unknown). */
export function resolveClient(text: string): ClientHit | null {
  if (!connectTokens(text).length) return null
  const alias = bestAlias(text)
  if (alias) {
    const tool = connectTool(alias.id)
    if (tool) return { tool, ...(alias.note ? { note: alias.note } : {}) }
  }
  const other = connectTool('other')
  return other ? { tool: other, unknown: true } : null
}

/** only a tool that is really NAMED in these words (never the open door), or null */
export function namedClient(text: string): ClientHit | null {
  const hit = resolveClient(text)
  return hit && !hit.unknown ? hit : null
}
